import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Slider } from 'primereact/slider';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import ProductCard from '../components/ProductCard';
import { EcommerceProductoService } from '../services/EcommerceProductoService';
import { EcommerceCategoriaService } from '../services/EcommerceCategoriaService';

const ProductList = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const queryCategory = searchParams.get('cat') || '';
    const querySearch = searchParams.get('q') || '';

    const [selectedCategories, setSelectedCategories] = useState(queryCategory ? [queryCategory] : []);
    const [priceRange, setPriceRange] = useState([0, 3000]);
    const [sortBy, setSortBy] = useState('featured');
    const [mobileFilters, setMobileFilters] = useState(false);
    
    // Estado para productos y categorías del backend
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const idEmpresa = 1; // ID de la empresa

    // Cargar productos y categorías del backend - SIN FALLBACK A MOCKDATA
    useEffect(() => {
        const loadData = async () => {
            try {
                setError(null);
                
                const [productsResponse, categoriesResponse] = await Promise.all([
                    EcommerceProductoService.obtenerProductos(idEmpresa),
                    EcommerceCategoriaService.obtenerCategorias(idEmpresa)
                ]);
                

                
                setAllProducts(productsResponse || []);
                setAllCategories(categoriesResponse || []);
            } catch (error) {
                setError(`Error cargando datos: ${error.message}`);
                setAllProducts([]);
                setAllCategories([]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Update filters when URL changes
    useEffect(() => {
        if (queryCategory) setSelectedCategories([queryCategory]);
        else setSelectedCategories([]);
    }, [queryCategory]);

    const sortOptions = [
        { label: 'Destacados', value: 'featured' },
        { label: 'Precio: Menor a Mayor', value: 'price-asc' },
        { label: 'Precio: Mayor a Menor', value: 'price-desc' },
        { label: 'Mejor valorados', value: 'rating' },
        { label: 'Mayor descuento', value: 'discount' },
        { label: 'Nombre A-Z', value: 'name' }
    ];

    const filteredProducts = useMemo(() => {
        // Usar SOLO productos del backend
        let result = querySearch 
            ? allProducts.filter(p => 
                p.nombre?.toLowerCase().includes(querySearch.toLowerCase())) 
            : [...allProducts];

        // Filter by category
        if (selectedCategories.length > 0) {
            result = result.filter(p => {
                // Comparar por ID o nombre de categoría
                return selectedCategories.some(cat => 
                    p.idCategoria?.toString() === cat || 
                    p.category === cat ||
                    p.nombre?.toLowerCase().includes(cat.toLowerCase())
                );
            });
        }

        // Filter by price
        result = result.filter(p => {
            const price = p.precioVenta || p.price || 0;
            return price >= priceRange[0] && price <= priceRange[1];
        });

        // Sort
        switch (sortBy) {
            case 'price-asc':
                result.sort((a, b) => (a.precioVenta || a.price) - (b.precioVenta || b.price));
                break;
            case 'price-desc':
                result.sort((a, b) => (b.precioVenta || b.price) - (a.precioVenta || a.price));
                break;
            case 'rating':
                result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                break;
            case 'discount':
                result.sort((a, b) => (b.discount || 0) - (a.discount || 0));
                break;
            case 'name':
                result.sort((a, b) => (a.nombre || a.name || '').localeCompare(b.nombre || b.name || ''));
                break;
            case 'featured':
            default:
                result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
                break;
        }

        return result;
    }, [selectedCategories, priceRange, sortBy, querySearch, allProducts]);

    const onCategoryChange = (slug) => {
        setSelectedCategories(prev => {
            if (prev.includes(slug)) {
                return prev.filter(c => c !== slug);
            }
            return [...prev, slug];
        });
    };

    const clearFilters = () => {
        setSelectedCategories([]);
        setPriceRange([0, 3000]);
        setSortBy('featured');
        navigate('/tienda/catalogo');
    };

    const hasActiveFilters = selectedCategories.length > 0 || priceRange[0] > 0 || priceRange[1] < 3000;

    const FiltersContent = () => (
        <>
            {/* Categories */}
            <div className="ec-catalog-filter-group">
                <h4 className="ec-catalog-filter-title">Categorías</h4>
                {allCategories.map(cat => (
                    <div 
                        key={cat.id} 
                        onClick={() => onCategoryChange(cat.id?.toString())}
                        style={{
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 10,
                            padding: '12px 10px', 
                            cursor: 'pointer',
                            borderRadius: '6px',
                            border: `2px solid ${selectedCategories.includes(cat.id?.toString()) ? (cat.color || '#2196F3') : 'transparent'}`,
                            background: selectedCategories.includes(cat.id?.toString()) ? `${cat.color || '#2196F3'}15` : 'transparent',
                            marginBottom: '4px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = `${cat.color || '#2196F3'}10`;
                            e.currentTarget.style.borderColor = `${cat.color || '#2196F3'}50`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = selectedCategories.includes(cat.id?.toString()) ? `${cat.color || '#2196F3'}15` : 'transparent';
                            e.currentTarget.style.borderColor = selectedCategories.includes(cat.id?.toString()) ? (cat.color || '#2196F3') : 'transparent';
                        }}
                    >
                        <Checkbox
                            inputId={`cat-${cat.id}`}
                            checked={selectedCategories.includes(cat.id?.toString())}
                            onChange={() => onCategoryChange(cat.id?.toString())}
                        />
                        <label htmlFor={`cat-${cat.id}`} style={{
                            cursor: 'pointer', 
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            color: 'var(--ec-text-primary)',
                            flex: 1,
                            margin: 0,
                            marginLeft: '8px'
                        }}>
                            {cat.descripcion || cat.name || cat.nombre || `Categoría ${cat.id}`}
                        </label>
                        <span style={{
                            fontSize: '0.75rem', 
                            color: 'var(--ec-text-light)',
                            background: 'var(--ec-bg)', 
                            padding: '3px 8px', 
                            borderRadius: '12px',
                            whiteSpace: 'nowrap',
                            fontWeight: '600'
                        }}>
                            {allProducts.filter(p => p.idCategoria === cat.id).length}
                        </span>
                    </div>
                ))}
            </div>

            {/* Price Range */}
            <div className="ec-catalog-filter-group">
                <h4 className="ec-catalog-filter-title">Rango de precio</h4>
                <Slider
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.value)}
                    range
                    min={0}
                    max={3000}
                    step={50}
                />
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    marginTop: 12, fontSize: '0.85rem', fontWeight: 600
                }}>
                    <span style={{ color: 'var(--ec-primary)' }}>${priceRange[0]}</span>
                    <span style={{ color: 'var(--ec-primary)' }}>${priceRange[1]}</span>
                </div>
            </div>

            {/* Clear */}
            {hasActiveFilters && (
                <Button
                    label="Limpiar filtros"
                    icon="pi pi-filter-slash"
                    className="p-button-text p-button-sm"
                    style={{ width: '100%', marginTop: 8 }}
                    onClick={clearFilters}
                />
            )}
        </>
    );

    return (
        <div className="ec-catalog">
            <div className="ec-container">
                {/* Error Message */}
                {error && (
                    <Message severity="error" text={error} style={{ width: '100%', marginBottom: 24 }} />
                )}

                {/* Breadcrumb */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', color: 'var(--ec-text-secondary)', marginBottom: 24 }}>
                    <span style={{ cursor: 'pointer', color: 'var(--ec-primary)' }} onClick={() => navigate('/tienda')}>
                        Inicio
                    </span>
                    <i className="pi pi-angle-right" style={{ fontSize: '0.75rem' }}></i>
                    <span>Catálogo</span>
                    {querySearch && (
                        <>
                            <i className="pi pi-angle-right" style={{ fontSize: '0.75rem' }}></i>
                            <span>Resultados para "{querySearch}"</span>
                        </>
                    )}
                </div>

                <div className="ec-catalog-grid">
                    {/* Filters Sidebar (Desktop) */}
                    <div className="ec-catalog-filters" style={{ display: 'block' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                                <i className="pi pi-filter" style={{ marginRight: 8, color: 'var(--ec-primary)' }}></i>
                                Filtros
                            </h3>
                        </div>
                        <FiltersContent />
                    </div>

                    {/* Products */}
                    <div>
                        {/* Top Bar */}
                        <div className="ec-catalog-topbar">
                            <div className="ec-catalog-count">
                                Se encontraron <strong>{filteredProducts.length}</strong> productos
                                {querySearch && <span> para "<strong>{querySearch}</strong>"</span>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {/* Mobile filter toggle */}
                                <Button
                                    icon="pi pi-filter"
                                    label="Filtros"
                                    className="p-button-outlined p-button-sm"
                                    style={{ display: 'none' }}
                                    id="ec-mobile-filter-btn"
                                    onClick={() => setMobileFilters(!mobileFilters)}
                                />
                                <Dropdown
                                    value={sortBy}
                                    options={sortOptions}
                                    onChange={(e) => setSortBy(e.value)}
                                    placeholder="Ordenar por"
                                    style={{ minWidth: 200 }}
                                />
                            </div>
                        </div>

                        {/* Mobile Filters (expandable) */}
                        {mobileFilters && (
                            <div style={{
                                background: 'var(--ec-bg-card)',
                                borderRadius: 'var(--ec-radius)',
                                border: '1px solid var(--ec-border)',
                                padding: 24,
                                marginBottom: 20
                            }}>
                                <FiltersContent />
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <div style={{
                                textAlign: 'center', padding: '64px 24px',
                                background: 'var(--ec-bg-card)',
                                borderRadius: 'var(--ec-radius)',
                                border: '1px solid var(--ec-border)'
                            }}>
                                <ProgressSpinner />
                            </div>
                        )}

                        {/* Products Grid */}
                        {!loading && filteredProducts.length === 0 ? (
                            <div style={{
                                textAlign: 'center', padding: '64px 24px',
                                background: 'var(--ec-bg-card)',
                                borderRadius: 'var(--ec-radius)',
                                border: '1px solid var(--ec-border)'
                            }}>
                                <i className="pi pi-search" style={{ fontSize: '3rem', color: 'var(--ec-border)', marginBottom: 16, display: 'block' }}></i>
                                <h3 style={{ color: 'var(--ec-text)', marginBottom: 8 }}>No se encontraron productos</h3>
                                <p style={{ color: 'var(--ec-text-secondary)', marginBottom: 24 }}>
                                    Intenta ajustar los filtros o buscar algo diferente
                                </p>
                                <Button
                                    label="Limpiar filtros"
                                    icon="pi pi-filter-slash"
                                    className="p-button-outlined"
                                    onClick={clearFilters}
                                />
                            </div>
                        ) : (
                            <div className="ec-catalog-products ec-animate-fade">
                                {filteredProducts.map(product => (
                                    <ProductCard key={product.id} product={product} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductList;
